import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { ExistingUserData } from '@/domains/user/components/ExistingUserInfoCard/types';
import { CreateBeneficiaryAutomaticallyRequest } from '../types/api/requests/CreateBeneficiaryAutomaticallyRequest';
import { CreateBeneficiaryManuallyRequest } from '../types/api/requests/CreateBeneficiaryManuallyRequest';
import {
  CreateBeneficiaryFieldKey,
  CreateBeneficiaryFieldValue,
  CreateBeneficiaryState,
  CreateBeneficiaryType,
} from './types/createBeneficiarySliceTypes';
import { AddPotentialBeneficiaryModalSteps } from '@/features/serviceModal/components/AddPotentialBeneficiary/enums';
import { createBeneficiaryInitialState } from './constants/createBeneficiarySliceConstants';

const createBeneficiarySlice = createSlice({
  name: 'createBeneficiary',
  initialState: createBeneficiaryInitialState,
  reducers: {
    changeCreateBeneficiaryType: (state: CreateBeneficiaryState, action: PayloadAction<CreateBeneficiaryType>) => {
      state.createBeneficiaryType = action.payload;
    },
    updateCreateBeneficiaryStepValues: <K extends CreateBeneficiaryFieldKey>(
      state: CreateBeneficiaryState,
      action: PayloadAction<{ step: K; value: CreateBeneficiaryFieldValue<K> }>,
    ) => {
      const { step, value } = action.payload;
      state.createBeneficiarySteps[step] = value;
    },
    resetCreateBeneficiaryState: () => createBeneficiaryInitialState,
    setUserForBeneficiaryCreate: (state: CreateBeneficiaryState, action: PayloadAction<ExistingUserData | null>) => {
      state.userForBeneficiaryCreate = action.payload;
    },
    createBeneficiaryManually: (_, _action: PayloadAction<CreateBeneficiaryManuallyRequest>) => {},
    createBeneficiaryAutomatically: (_, _action: PayloadAction<CreateBeneficiaryAutomaticallyRequest>) => {},
    createBeneficiarySuccess: () => {},
    setCreateBeneficiaryLoadingStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.statuses.isCreateBeneficiaryLoading = payload;
    },
  },
  selectors: {
    selectCreateBeneficiarySteps: (state) => state.createBeneficiarySteps,
    selectUserForBeneficiaryCreate: (state) => state.userForBeneficiaryCreate,
    selectCreateBeneficiaryType: (state) => state.createBeneficiaryType,
    selectCreateBeneficiaryIsLoading: (state) => state.statuses.isCreateBeneficiaryLoading,
    selectCreateBeneficiaryAddressDetails: (state) =>
      state.createBeneficiarySteps[AddPotentialBeneficiaryModalSteps.AddressInfo],
  },
});

export const {
  updateCreateBeneficiaryStepValues,
  resetCreateBeneficiaryState,
  setUserForBeneficiaryCreate,
  changeCreateBeneficiaryType,
  createBeneficiaryManually,
  createBeneficiaryAutomatically,
  createBeneficiarySuccess,
  setCreateBeneficiaryLoadingStatus,
} = createBeneficiarySlice.actions;

export const {
  selectCreateBeneficiarySteps,
  selectUserForBeneficiaryCreate,
  selectCreateBeneficiaryType,
  selectCreateBeneficiaryIsLoading,
  selectCreateBeneficiaryAddressDetails,
} = createBeneficiarySlice.selectors;

export const createBeneficiaryReducer = createBeneficiarySlice.reducer;
export const createBeneficiaryReducerPath = createBeneficiarySlice.reducerPath;
